// useNocStream — Server-Sent Events client for the home page (Phase 1G).
//
// Strategy:
//   - Open EventSource on /api/v2/noc/stream.
//   - On error, fall back to a 10s polling loop hitting /api/v2/noc/snapshot.
//   - Expose `{ data, isLive }` so the UI can decorate the header with a
//     live-dot when SSE is healthy and a "Polling 10s" pill when it isn't.
import { useEffect, useRef, useState } from "react";

import { api } from "./api";

export interface NocRecentAudit {
    id: number;
    ts: string;
    action: string;
    actorEmail?: string;
    actorKind: string;
    resource: string;
}

export interface NocPayload {
    generatedAt: string;
    iocsTotal: number;
    sightings1h: number;
    sightings24h: number;
    cvesTotal: number;
    feedsTotal: number;
    feedsHealthy: number;
    feedsFailing: number;
    recentAudit: NocRecentAudit[];
}

export interface UseNocStreamResult {
    data: NocPayload | null;
    isLive: boolean;
    isLoading: boolean;
    error: Error | null;
}

export function useNocStream(): UseNocStreamResult {
    const [data, setData] = useState<NocPayload | null>(null);
    const [isLive, setIsLive] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [error, setError] = useState<Error | null>(null);
    const sseRef = useRef<EventSource | null>(null);
    const pollRef = useRef<number | null>(null);

    useEffect(() => {
        let cancelled = false;

        const stopPoll = (): void => {
            if (pollRef.current !== null) {
                window.clearInterval(pollRef.current);
                pollRef.current = null;
            }
        };
        const stopSSE = (): void => {
            if (sseRef.current !== null) {
                sseRef.current.close();
                sseRef.current = null;
            }
        };

        const startPolling = (): void => {
            stopSSE();
            setIsLive(false);
            const tick = async (): Promise<void> => {
                try {
                    const p = await api.get<NocPayload>("/api/v2/noc/snapshot");
                    if (cancelled) return;
                    setData(p);
                    setError(null);
                    setIsLoading(false);
                } catch (err) {
                    if (cancelled) return;
                    setError(err instanceof Error ? err : new Error(String(err)));
                    setIsLoading(false);
                }
            };
            void tick();
            pollRef.current = window.setInterval(tick, 10_000);
        };

        const startSSE = (): void => {
            stopPoll();
            try {
                const es = new EventSource("/api/v2/noc/stream", { withCredentials: true });
                sseRef.current = es;
                const onPayload = (e: MessageEvent<string>): void => {
                    try {
                        const parsed = JSON.parse(e.data) as NocPayload;
                        if (cancelled) return;
                        setData(parsed);
                        setError(null);
                        setIsLoading(false);
                        setIsLive(true);
                    } catch {
                        // ignore malformed events; the next tick replaces them
                    }
                };
                es.addEventListener("snapshot", onPayload as EventListener);
                es.addEventListener("metrics", onPayload as EventListener);
                es.addEventListener("error", () => {
                    if (cancelled) return;
                    // Browser auto-reconnects; only fall back if it errors and
                    // is in CLOSED state — i.e. the server returned a non-2xx.
                    if (es.readyState === EventSource.CLOSED) {
                        startPolling();
                    } else {
                        setIsLive(false);
                    }
                });
            } catch {
                startPolling();
            }
        };

        startSSE();

        return () => {
            cancelled = true;
            stopSSE();
            stopPoll();
        };
    }, []);

    return { data, isLive, isLoading, error };
}
